type AnchorRoot = HTMLElement;
type AnchorHeading = HTMLElement;

interface AnchorItem {
	heading: AnchorHeading;
	link: HTMLAnchorElement;
}

const SELECTOR_ROOT = '.bloc-liste-ancres';
const SELECTOR_LIST = '.bloc-liste-ancres__list';

const CLASS_ITEM = 'bloc-liste-ancres__item';
const CLASS_LINK = 'bloc-liste-ancres__link';
const CLASS_LINK_INDEX = 'bloc-liste-ancres__link-index';
const CLASS_LINK_LABEL = 'bloc-liste-ancres__link-label';

function slugify(value: string): string {
	const slug = value
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

	return slug || 'section';
}

function formatIndex(index: number): string {
	return String(index + 1).padStart(2, '0');
}

function getScrollOffset(root: AnchorRoot): number {
	const rawOffset = root.dataset.scrollOffset;

	if (!rawOffset) {
		return 96;
	}

	const offset = Number.parseInt(rawOffset, 10);

	return Number.isNaN(offset) ? 96 : offset;
}

function getColorIndex(root: AnchorRoot): string {
	const color = root.dataset.color;

	return color || '#3d2ede';
}

function getContainer(root: AnchorRoot): ParentNode {
	const selector = root.dataset.anchorContainer || 'main';

	return document.querySelector(selector) || document.body;
}

function getHeadingSelector(root: AnchorRoot): string {
	return root.dataset.anchorHeadingSelector || 'h2[data-ancre-active]';
}

function getHeadingLabel(heading: AnchorHeading): string {
	const dataLabel = heading.dataset.ancre?.trim();

	if (dataLabel) {
		return dataLabel;
	}

	return heading.textContent?.trim() || '';
}

function isActiveAnchorHeading(heading: AnchorHeading): boolean {
	return heading.tagName.toLowerCase() === 'h2' && heading.hasAttribute('data-ancre-active');
}

function getHeadings(root: AnchorRoot): AnchorHeading[] {
	const container = getContainer(root);
	const selector = getHeadingSelector(root);

	return Array.from(container.querySelectorAll<AnchorHeading>(selector)).filter((heading) => {
		if (!isActiveAnchorHeading(heading)) {
			return false;
		}

		if (root.contains(heading)) {
			return false;
		}

		if (heading.closest('[hidden], [aria-hidden="true"]')) {
			return false;
		}

		const label = getHeadingLabel(heading);

		return Boolean(label);
	});
}

function ensureHeadingId(heading: AnchorHeading, label: string): string {
	if (heading.id) {
		return heading.id;
	}

	const baseId = slugify(label);
	let id = baseId;
	let index = 2;

	while (document.getElementById(id)) {
		id = `${baseId}-${index}`;
		index += 1;
	}

	heading.id = id;

	return id;
}

function makeHeadingFocusable(heading: AnchorHeading): void {
	if (!heading.hasAttribute('tabindex')) {
		heading.setAttribute('tabindex', '-1');
	}
}

function createAnchorItem(heading: AnchorHeading, index: number): AnchorItem {
	const label = getHeadingLabel(heading);
	const id = ensureHeadingId(heading, label);

	makeHeadingFocusable(heading);

	const item = document.createElement('li');
	item.className = CLASS_ITEM;

	const link = document.createElement('a');
	link.className = CLASS_LINK;
	link.href = `#${id}`;

	const indexElement = document.createElement('span');
	indexElement.className = CLASS_LINK_INDEX;
	indexElement.textContent = formatIndex(index);

	const labelElement = document.createElement('span');
	labelElement.className = CLASS_LINK_LABEL;
	labelElement.textContent = label;

	link.appendChild(indexElement);
	link.appendChild(labelElement);
	item.appendChild(link);

	return {
		heading,
		link,
	};
}

function setActiveItem(items: AnchorItem[], activeItem: AnchorItem | null): void {
	items.forEach((item) => {
		if (item === activeItem) {
			item.link.setAttribute('aria-current', 'true');
		} else {
			item.link.removeAttribute('aria-current');
		}
	});
}

function getCurrentItem(items: AnchorItem[], offset: number): AnchorItem | null {
	let currentItem = items[0] || null;

	items.forEach((item) => {
		const rect = item.heading.getBoundingClientRect();

		if (rect.top <= offset + 1) {
			currentItem = item;
		}
	});

	return currentItem;
}

function scrollToHeading(root: AnchorRoot, heading: AnchorHeading): void {
	const offset = getScrollOffset(root);
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const targetTop = heading.getBoundingClientRect().top + window.scrollY - offset;

	window.scrollTo({
		top: targetTop,
		behavior: prefersReducedMotion ? 'auto' : 'smooth',
	});

	heading.focus({
		preventScroll: true,
	});

	window.history.pushState(null, '', `#${heading.id}`);
}

function initAnchorLinks(root: AnchorRoot, items: AnchorItem[]): void {
	items.forEach((item) => {
		item.link.addEventListener('click', (event) => {
			event.preventDefault();

			setActiveItem(items, item);
			scrollToHeading(root, item.heading);
		});
	});
}

function initActiveState(root: AnchorRoot, items: AnchorItem[]): void {
	let ticking = false;

	const update = (): void => {
		const offset = getScrollOffset(root);
		const currentItem = getCurrentItem(items, offset);

		setActiveItem(items, currentItem);
		ticking = false;
	};

	const requestUpdate = (): void => {
		if (ticking) {
			return;
		}

		ticking = true;
		window.requestAnimationFrame(update);
	};

	update();

	window.addEventListener('scroll', requestUpdate, {
		passive: true,
	});

	window.addEventListener('resize', requestUpdate);
	window.addEventListener('hashchange', requestUpdate);
}

function exposeOffsetToCss(root: AnchorRoot): void {
	const offset = getScrollOffset(root);

	document.documentElement.style.setProperty(
		'--bloc-liste-ancres-offset',
		`${offset}px`
	);
}

function exposeColorToCss(root: AnchorRoot): void {
	const color = getColorIndex(root);

	root.style.setProperty(
		'--bloc-liste-ancres-color',
		color
	);
}

function initRoot(root: AnchorRoot): void {
	if (root.dataset.initialized === 'true') {
		return;
	}

	const list = root.querySelector<HTMLUListElement>(SELECTOR_LIST);

	if (!list) {
		return;
	}

	const headings = getHeadings(root);

	if (!headings.length) {
		root.hidden = true;
		return;
	}

	list.innerHTML = '';

	const items = headings.map((heading, index) => {
		const anchorItem = createAnchorItem(heading, index);
		const item = anchorItem.link.parentElement;

		if (item) {
			list.appendChild(item);
		}

		return anchorItem;
	});

	root.hidden = false;
	root.dataset.initialized = 'true';

	exposeOffsetToCss(root);
	exposeColorToCss(root);
	initAnchorLinks(root, items);
	initActiveState(root, items);
}

export function initBlocListeAncres(): void {
	const roots = document.querySelectorAll<AnchorRoot>(SELECTOR_ROOT);

	roots.forEach((root) => {
		initRoot(root);
	});
}
